import { BalanceReportSortingFindInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-sorting/balance-report-sorting-find.dto';
import { BalanceReportSortingFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-sorting/balance-report-sorting-form.dto';
import { BalanceReportSheetQueryInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report/balance-report-sheet-query.dto';
import { BalanceReportSheetInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report/balance-report-sheet.dto';
import { BalanceReportFormatEnum } from '@zaoblako/shared/modules/finance/enums/balance-report-format.enum';
import { BaseService } from '@zaoblako/web/core/services/base.service';
import type { BalanceReportInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report/balance-report.dto';
import type { BalanceReportQueryInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report/balance-report-query.dto';
import type { BalanceReportSettingsFindInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-settings/balance-report-settings-find.dto';
import type { BalanceReportSettingsFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-settings/balance-report-settings-form.dto';
import type { BalanceReportCustomRowFindInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-custom-row/balance-report-custom-row-find.dto';
import type { WithDeletedQueryInterface } from '@zaoblako/shared/common/dtos/with-deleted-query.dto';
import type { BalanceReportCustomRowFormInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-custom-row/balance-report-custom-row-form.dto';
import type { BalanceReportRowTokenInterface } from '@zaoblako/shared/modules/finance/dtos/balance-report-custom-row/balance-report-row-token.dto';

import { Injectable } from '@angular/core';
import type { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BalanceReportService extends BaseService {
  protected baseUrl = '/finance/plan-fact/report/balance';

  /** Get balance report itself */
  public getReport(params: BalanceReportQueryInterface): Observable<BalanceReportInterface> {
    return this.http.get<BalanceReportInterface>(this.baseUrl, { params: this.objectToHttpParams(params) });
  }

  // Balance report settings APIs

  /** Get balance report settings for current authorized user */
  public getReportSettings(): Observable<BalanceReportSettingsFindInterface> {
    return this.http.get<BalanceReportSettingsFindInterface>(`${this.baseUrl}/settings`);
  }

  /** Set balance report settings for current authorized user */
  public setReportSettings(body: BalanceReportSettingsFormInterface): Observable<BalanceReportSettingsFindInterface> {
    return this.http.put<BalanceReportSettingsFindInterface>(`${this.baseUrl}/settings`, body);
  }

  // Custom rows APIs

  /** Find all available custom rows for balance report */
  public findManyCustomRows(query?: WithDeletedQueryInterface): Observable<BalanceReportCustomRowFindInterface[]> {
    return this.http.get<BalanceReportCustomRowFindInterface[]>(`${this.baseUrl}/custom-row`, {
      params: query ? this.objectToHttpParams(query) : {}
    });
  }

  /** Find one custom row for balance report by identifier */
  public findOneCustomRow(id: number): Observable<BalanceReportCustomRowFindInterface> {
    return this.http.get<BalanceReportCustomRowFindInterface>(`${this.baseUrl}/custom-row/${id}`);
  }

  /** Create one custom row for balance report */
  public createOneCustomRow(
    body: BalanceReportCustomRowFormInterface
  ): Observable<BalanceReportCustomRowFindInterface> {
    return this.http.post<BalanceReportCustomRowFindInterface>(`${this.baseUrl}/custom-row`, body);
  }

  /** Update one custom row for balance report by identifier */
  public updateOneCustomRow(
    id: number,
    body: BalanceReportCustomRowFormInterface
  ): Observable<BalanceReportCustomRowFindInterface> {
    return this.http.put<BalanceReportCustomRowFindInterface>(`${this.baseUrl}/custom-row/${id}`, body);
  }

  /** Delete one custom row for balance report by identifier */
  public deleteOneCustomRow(id: number): Observable<BalanceReportCustomRowFindInterface> {
    return this.http.delete<BalanceReportCustomRowFindInterface>(`${this.baseUrl}/custom-row/${id}`);
  }

  /** Restore one deleted custom row for balance report by identifier */
  public restoreOneCustomRow(id: number): Observable<BalanceReportCustomRowFindInterface> {
    return this.http.post<BalanceReportCustomRowFindInterface>(`${this.baseUrl}/custom-row/${id}/restore`, {});
  }

  /** Get all available tokens which can be added to custom row expression */
  public getCustomRowTokens(): Observable<BalanceReportRowTokenInterface[]> {
    return this.http.get<BalanceReportRowTokenInterface[]>(`${this.baseUrl}/custom-row/tokens`);
  }

  public getSorting(format: BalanceReportFormatEnum): Observable<BalanceReportSortingFindInterface> {
    return this.http.get<BalanceReportSortingFindInterface>(`${this.baseUrl}/sorting/${format}`);
  }

  public setSorting(
    format: BalanceReportFormatEnum,
    body: BalanceReportSortingFormInterface
  ): Observable<BalanceReportSortingFindInterface> {
    return this.http.put<BalanceReportSortingFindInterface>(`${this.baseUrl}/sorting/${format}`, body);
  }

  public getReportSheet(params: BalanceReportSheetQueryInterface): Observable<BalanceReportSheetInterface> {
    return this.http.get<BalanceReportSheetInterface>(`${this.baseUrl}/sheet`, {
      params: this.objectToHttpParams(params)
    });
  }
}
